import { Box, Flex, Spinner, Text, useMediaQuery, useSlotRecipe } from '@chakra-ui/react';

import { RANGE_PICKER } from '@nocowanie/core/consts';
import { dateHelpers } from '@nocowanie/core/helpers';
import { IconsLinear } from '@nocowanie/icons/linear';
import { IconsNocowanie } from '@nocowanie/icons/nocowanie';

import { SearchEngineMobileProps } from './search-engine-mobile.props';

import { getFlexDateLabel } from '../../../helpers';
import { usePluralisationTranslation } from '../../../hooks';
import { defaultSearchEngineTranslation } from '../../../models';
import { SearchEngineInstantReservationCheckbox } from '../../molecules/search-engine-instant-reservation-checkbox';

export const SearchEngineMobile = ({
    setValue,
    getValues,
    watch,
    onInputValueUpdated,
    translationData = defaultSearchEngineTranslation,
    handleDrawerOpen,
    isDrawerPending,
    isMapSearch = false,
    hideInstantReservationInput = false,
    withListing = false,
    isSubdomainVariant = false,
    locationPlaceholder,
    wrapTrigger,
    ...props
}: SearchEngineMobileProps) => {
    const recipe = useSlotRecipe({
        key: 'searchEngineMobile',
    });
    const styles = recipe();
    const [isXsBreakpoint] = useMediaQuery(['(max-width: 375px)']);

    const openDrawer = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        handleDrawerOpen();
    };

    const displayedDateFormat = 'dd MMM';
    const pluralizeTranslation = usePluralisationTranslation('accommodation', 'guestsNightsLabel');
    const guestsLabel = getValues('guests.adultsCount')
        ? pluralizeTranslation(
              getValues('guests.adultsCount') + getValues('guests.childrenCount'),
              'person',
          )
        : translationData.guestsPickerLabel;
    const locationValue = getValues('location');
    const checkInDate = getValues('dates.checkInDate');
    const checkOutDate = getValues('dates.checkOutDate');
    const flexDates = getValues('flexDates') ?? RANGE_PICKER.FLEX_DATES.DEFAULT;

    const shouldShowFlexDatesLabel = flexDates > RANGE_PICKER.FLEX_DATES.DEFAULT;
    const flexDatesLabel = shouldShowFlexDatesLabel
        ? getFlexDateLabel(flexDates, translationData, isXsBreakpoint)
        : '';

    const containerStyles = withListing
        ? styles.listingSearchContainer
        : isMapSearch
          ? styles.mapSearchContainer
          : isSubdomainVariant
            ? styles.subdomainSearchContainer
            : styles.container;

    const trigger = (
        <Flex
            css={containerStyles}
            as="button"
            onClick={openDrawer}
            data-testid={'search-engine-mobile-trigger'}
            className={'wc-search-engine__mobile-trigger'}
            {...props}
        >
            <Flex direction={'column'} gap={0}>
                <Text as={'span'} fontSize={'md'} textAlign={'left'} fontWeight={'semibold'}>
                    {locationValue?.length
                        ? locationValue
                        : locationPlaceholder || translationData.locationPlaceholder}
                </Text>
                <Flex gap={3} fontSize={'sm'} color={'gray.600'}>
                    <Flex gap={1} css={styles.datesWrapper}>
                        {checkInDate && checkOutDate ? (
                            <>
                                <Text as="span" lineHeight={1}>
                                    {dateHelpers.format(checkInDate, displayedDateFormat)}
                                </Text>
                                -
                                <Text as="span" lineHeight={1}>
                                    {dateHelpers.format(checkOutDate, displayedDateFormat)}
                                </Text>
                                {shouldShowFlexDatesLabel && (
                                    <Text as={'span'} my={-0.25} ml={isXsBreakpoint ? 1 : 0}>
                                        (<IconsNocowanie.PlusMinus scale={0.6} mx={-1} />
                                        {flexDatesLabel})
                                    </Text>
                                )}
                            </>
                        ) : (
                            translationData.datesPlaceholder
                        )}
                    </Flex>
                    <Flex gap={isXsBreakpoint ? 1 : 2} alignItems={'center'}>
                        <Text as="span" lineHeight={1}>
                            {guestsLabel}
                        </Text>
                    </Flex>
                </Flex>
            </Flex>
            {isDrawerPending ? (
                <Spinner {...styles.icon} size="md" />
            ) : (
                <IconsLinear.Search fontSize={'2xl'} {...styles.icon} />
            )}
        </Flex>
    );

    return (
        <Box>
            {wrapTrigger ? wrapTrigger(trigger) : trigger}
            {!hideInstantReservationInput ? (
                <SearchEngineInstantReservationCheckbox
                    onInputValueUpdated={onInputValueUpdated}
                    setValue={setValue}
                    watch={watch}
                    withBackground={true}
                />
            ) : null}
        </Box>
    );
};
